import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../session/SessionContext";
import { EmptyState, ErrorState, LoadingState, PermissionDeniedState } from "../components/states";
import { fetchMcpConnections, revokeMcpConnection, type McpConnection } from "../api/mcpConnections";
import { useToast } from "../components/toast/toastContext";
import styles from "./ConnectedApplicationsPage.module.css";
import { unchangedOr, usePolling } from "../hooks/usePolling";

type PageState =
  | { kind: "loading" }
  | { kind: "loaded"; connections: McpConnection[] }
  | { kind: "unavailable" }
  | { kind: "error"; message: string };

// Renewal is explained on the consent screen, not listed as an area of access.
const RenewalScope = "offline_access";

/**
 * The "Connected applications" screen: the AI applications the signed-in person has approved to act for them,
 * what each was approved for, and a button that disconnects one. Disconnecting revokes that application's
 * access and renewal at once; it does not sign the person out or touch any other application. Every signed-in
 * person can use it for their own connections only.
 */
export function ConnectedApplicationsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [revoking, setRevoking] = useState<string | null>(null);
  const { show } = useToast();
  const signedIn = snapshot.status === "signed-in";

  // A quiet load keeps what is shown when it fails.
  const load = useCallback(async (quiet = false) => {
    const result = await fetchMcpConnections();
    if (result.kind === "ok") {
      const next: PageState = { kind: "loaded", connections: result.response };
      setState((previous) => unchangedOr(previous, next));
    } else if (quiet) {
      return;
    } else if (result.kind === "unavailable") {
      setState({ kind: "unavailable" });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, []);

  useEffect(() => {
    if (signedIn) {
      void load();
    }
  }, [signedIn, load]);

  usePolling(() => load(true), signedIn && state.kind === "loaded" && revoking === null);

  const disconnect = async (connection: McpConnection) => {
    setRevoking(connection.id);
    const result = await revokeMcpConnection(connection.id);
    setRevoking(null);
    if (result.kind === "error") {
      show(result.message, "error");
      return;
    }
    // A connection that is already gone ("unavailable") is the outcome the person wanted.
    show(t("connectedApps.disconnected", { name: connection.clientName }), "success");
    await load();
  };

  if (!signedIn) {
    return <PermissionDeniedState title={t("connectedApps.denied.title")} description={t("connectedApps.denied.description")} />;
  }

  if (state.kind === "loading") {
    return <LoadingState label={t("connectedApps.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState description={state.message} onRetry={() => void load()} />;
  }

  if (state.kind === "unavailable") {
    return <EmptyState title={t("connectedApps.unavailable.title")} description={t("connectedApps.unavailable.description")} />;
  }

  return (
    <section className={styles.page} aria-labelledby="connected-apps-heading">
      <h1 id="connected-apps-heading">{t("connectedApps.title")}</h1>
      <p className={styles.note}>{t("connectedApps.explanation")}</p>
      {state.connections.length === 0 ? (
        <EmptyState title={t("connectedApps.empty.title")} description={t("connectedApps.empty.description")} />
      ) : (
        <ul className={styles.list}>
          {state.connections.map((connection) => (
            <li key={connection.id} className={styles.item}>
              <div className={styles.details}>
                <h2 className={styles.name}>{connection.clientName}</h2>
                {connection.connectedAt !== null && (
                  <p className={styles.note}>
                    {t("connectedApps.connectedOn", { date: new Date(connection.connectedAt).toLocaleDateString() })}
                  </p>
                )}
                <p className={styles.note}>{t("connectedApps.canDo")}</p>
                <ul className={styles.scopes}>
                  {connection.scopes
                    .filter((scope) => scope !== RenewalScope)
                    .map((scope) => (
                      <li key={scope}>
                        {t(`connectedApps.scopes.${scope}`, { defaultValue: scope })}
                      </li>
                    ))}
                </ul>
              </div>
              <button
                type="button"
                className="button button-secondary"
                disabled={revoking === connection.id}
                aria-label={t("connectedApps.disconnectNamed", { name: connection.clientName })}
                onClick={() => void disconnect(connection)}
              >
                {t("connectedApps.disconnect")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
